import { useQueryClient } from "@tanstack/react-query";

import { API_CACHE_KEYS } from "api/cacheKeys";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { AccountMember } from "types/accountMembers";
import { ListResponse } from "types/api";

import { BaseQueryOptions, CursorPaginationParams } from "../types";

export type AccountMembersParams = CursorPaginationParams & {
  ordering?: string;
};

type UseGetAccountMembersParams = {
  params?: AccountMembersParams;
  options?: BaseQueryOptions<ListResponse<AccountMember>>;
};

const useGetAccountMembers = ({ params, options }: UseGetAccountMembersParams = {}) => {
  const searchParams = new URLSearchParams();

  if (params?.paginate_from !== undefined) {
    searchParams.append("paginate_from", params.paginate_from.toString());
  }

  if (params?.page_size !== undefined) {
    searchParams.append("page_size", params.page_size.toString());
  }

  if (params?.ordering !== undefined) {
    searchParams.append("ordering", params.ordering.toString());
  }

  const url = `${JANZU_URL}reef-api/account/members?${searchParams.toString()}`;

  return useCustomQuery<ListResponse<AccountMember>>(API_CACHE_KEYS.accountMembers.list(params), url, options);
};

type CreateAccountMemberParams = {
  email: string;
  role: string;
};

type CreateAccountMemberResponse = {
  created_email: string;
};

type CreateAccountMemberError = {
  error: string;
};

const useCreateAccountMember = () => {
  const queryClient = useQueryClient();

  return useCustomMutation<CreateAccountMemberResponse, CreateAccountMemberParams, CreateAccountMemberError>(
    `${JANZU_URL}reef-api/account/members`,
    "POST",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: API_CACHE_KEYS.accountMembers.all,
        });
      },
    }
  );
};

type UpdateAccountMemberParams = {
  id: number;
  role?: string;
  is_active?: boolean;
};

const useUpdateAccountMember = () => {
  const queryClient = useQueryClient();

  return useCustomMutation<AccountMember, UpdateAccountMemberParams>(
    ({ id }: UpdateAccountMemberParams) => `${JANZU_URL}reef-api/account/members/${id}`,
    "PATCH",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: API_CACHE_KEYS.accountMembers.all,
        });
      },
    }
  );
};

type UseAccountMembersParams = {
  getOptions?: {
    params?: AccountMembersParams;
    options?: BaseQueryOptions<ListResponse<AccountMember>>;
  };
};

export const useAccountMembers = ({ getOptions }: UseAccountMembersParams = {}) => {
  const accountMembers = useGetAccountMembers(getOptions);
  const createAccountMember = useCreateAccountMember();
  const updateAccountMember = useUpdateAccountMember();

  return {
    // Get operations
    accountMembers: accountMembers.data,
    isLoadingAccountMembers: accountMembers.isLoading,
    isFetchingAccountMembers: accountMembers.isFetching,
    accountMembersError: accountMembers.error,
    refetchAccountMembers: accountMembers.refetch,

    // Create operation
    createAccountMember: createAccountMember.mutate,
    createAccountMemberAsync: createAccountMember.mutateAsync,
    isCreating: createAccountMember.isLoading,
    createAccountMemberError: createAccountMember.error,

    // Update operations
    updateAccountMember: updateAccountMember.mutate,
    updateAccountMemberAsync: updateAccountMember.mutateAsync,
    isUpdating: updateAccountMember.isLoading,
    updateAccountMemberError: updateAccountMember.error,

    // Combined loading state
    isLoading: createAccountMember.isLoading || updateAccountMember.isLoading,
  };
};
